/**
 * V176 — AI Autonomy Live Stats Ticker
 *
 * Public landing-page widget showing real aggregate stats from
 * `db.agent_autonomy_events`. Polls `/api/agent-autonomy/live-stats`
 * every 30s and animates the headline number on each update.
 *
 * Why this matters: stronger social proof than testimonials —
 * visitors see the platform actually doing work, not marketing copy.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Activity, Zap, Clock, CheckCircle, FileText, MessageSquareReply, Package, ChevronDown } from "lucide-react";
import { API } from "../config";

const POLL_MS = 30_000;

function useCountUp(target, durationMs = 1200) {
  const [val, setVal] = useState(target ?? 0);
  const fromRef = useRef(target ?? 0);
  useEffect(() => {
    if (target == null) return;
    const from = fromRef.current;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out-cubic
      const v = Math.round(from + (target - from) * eased);
      setVal(v);
      if (p < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return val;
}

export default function AutonomyLiveTicker() {
  const [stats, setStats] = useState(null);
  const [pulse, setPulse] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/agent-autonomy/live-stats`);
      setStats(r.data);
      setPulse(true);
      setTimeout(() => setPulse(false), 800);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, []);

  const headline = useCountUp(stats?.emails_sent_all_time);
  const last24 = useCountUp(stats?.emails_sent_24h);

  if (!stats) return null;

  return (
    <section className="relative bg-gradient-to-br from-gray-900 via-violet-950 to-black text-white py-12 sm:py-16 overflow-hidden"
             data-testid="autonomy-live-ticker">
      {/* Decorative noise + glow */}
      <div className="absolute inset-0 opacity-30 pointer-events-none"
           style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(168,85,247,0.25), transparent 50%), radial-gradient(circle at 80% 70%, rgba(236,72,153,0.18), transparent 50%)" }} />
      <div className="absolute inset-0 pointer-events-none"
           style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")", opacity: 0.04 }} />

      <div className="relative max-w-6xl mx-auto px-5">
        <div className="flex items-center gap-2 mb-2">
          <span className={`inline-flex w-2 h-2 rounded-full bg-emerald-400 ${pulse ? "animate-ping" : ""}`}
                data-testid="autonomy-pulse-dot" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-emerald-300 font-bold">Live · auto-refresh 30s</span>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black font-['Chivo'] leading-[1.05] tracking-tight max-w-3xl">
          Naši AI agenti práve odoslali
          <br/>
          <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent" data-testid="autonomy-headline">
            {headline.toLocaleString("sk-SK")}
          </span>{" "}
          <span className="text-gray-400 text-2xl sm:text-3xl lg:text-4xl">reply emailov</span>
        </h2>
        <p className="mt-3 text-sm sm:text-base text-gray-400 max-w-2xl">
          Toto nie je marketingový claim — sú to <strong className="text-white">reálne čísla</strong> z produkčnej databázy, agregované za poslednú dobu.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-8">
          <StatCard
            icon={Zap}
            color="violet"
            label="Posledných 24 h"
            value={`+${last24.toLocaleString("sk-SK")}`}
            sub={`${stats.events_count_24h} agent runs`}
            testid="autonomy-24h"
          />
          <StatCard
            icon={Clock}
            color="fuchsia"
            label="Priemerný čas na 1 email"
            value={`${stats.avg_seconds_per_item}s`}
            sub="oproti ~3 minútam manuálne"
            testid="autonomy-avg"
          />
          <StatCard
            icon={CheckCircle}
            color="emerald"
            label="Bez manuálnej úpravy"
            value={`${stats.no_manual_edit_pct}%`}
            sub="AI návrh prešiel rovno"
            testid="autonomy-no-edit"
          />
        </div>

        <div className="mt-7 flex items-center gap-2 text-xs text-gray-500">
          <Activity className="w-3.5 h-3.5" />
          <span>Posledná akcia: {stats.last_event_at
            ? new Date(stats.last_event_at).toLocaleString("sk-SK")
            : "—"}</span>
          {stats.top_event_type && (
            <span className="ml-2 px-2 py-0.5 bg-white/5 rounded font-mono text-[10px]">
              {stats.top_event_type}
            </span>
          )}
        </div>

        {/* V177 — per-agent breakdown mini stripes */}
        {(stats.breakdown || []).length > 0 && (
          <div className="mt-8 pt-7 border-t border-white/10" data-testid="autonomy-breakdown">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold mb-3">
              Rozdelenie podľa typu agenta
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {stats.breakdown.map((b) => (
                <AgentStripe key={b.agent_key} breakdown={b} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

const AGENT_ICONS = {
  blog_agent: FileText,
  returns_triage: MessageSquareReply,
  inventory_agent: Package,
};
const AGENT_COLORS = {
  blog_agent: "violet",
  returns_triage: "amber",
  inventory_agent: "emerald",
};

function _ago(sec) {
  if (sec == null) return "—";
  if (sec < 60) return `pred ${sec} s`;
  if (sec < 3600) return `pred ${Math.round(sec / 60)} min`;
  if (sec < 86400) return `pred ${Math.round(sec / 3600)} h`;
  return `pred ${Math.round(sec / 86400)} d`;
}

function AgentStripe({ breakdown: b }) {
  const [open, setOpen] = useState(false);
  const Icon = AGENT_ICONS[b.agent_key] || Activity;
  const color = AGENT_COLORS[b.agent_key] || "violet";
  const count = useCountUp(b.count_all_time);

  return (
    <div className="bg-white/5 backdrop-blur border border-white/10 rounded-lg overflow-hidden hover:border-white/20 transition-colors"
         data-testid={`autonomy-stripe-${b.agent_key}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-3 flex items-center gap-3 text-left"
        data-testid={`autonomy-stripe-toggle-${b.agent_key}`}>
        <div className={`w-8 h-8 rounded-lg bg-${color}-500/20 flex items-center justify-center shrink-0`}>
          <Icon className={`w-4 h-4 text-${color}-300`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-white text-sm leading-tight">{b.label}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            <span className={`text-${color}-300 font-bold`}>{count.toLocaleString("sk-SK")}</span> all-time
            {b.count_24h > 0 && (
              <span className="ml-2 text-emerald-300">+{b.count_24h} 24h</span>
            )}
          </p>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 border-t border-white/5 bg-black/20">
          {(b.recent || []).length === 0 ? (
            <p className="py-3 text-[11px] text-gray-500 italic">Zatiaľ žiadne nedávne akcie.</p>
          ) : (
            <ul className="space-y-1 py-2" data-testid={`autonomy-stripe-recent-${b.agent_key}`}>
              {b.recent.map((r, i) => (
                <li key={i} className="flex items-center gap-2 text-[11px]">
                  <span className={`inline-flex w-1.5 h-1.5 rounded-full ${r.ok ? "bg-emerald-400" : "bg-rose-400"}`} />
                  <span className="text-gray-300 font-mono">{r.event_label}</span>
                  <span className="text-gray-500 ml-auto">{_ago(r.ago_seconds)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, sub, testid }) {
  return (
    <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-5 hover:border-white/20 transition-colors"
         data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-7 h-7 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
          <Icon className={`w-4 h-4 text-${color}-300`} />
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">{label}</span>
      </div>
      <p className="text-3xl sm:text-4xl font-black font-['Chivo'] text-white leading-none">{value}</p>
      <p className="mt-2 text-[11px] text-gray-500">{sub}</p>
    </div>
  );
}
